import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { previewGoal, createGoal, getPortfolioReturns, fmtCop, horizonLabel, portfolioLabel } from '../lib/goals';

const NAVY = '#0A2540';
const SOFT = '#F9FAFB';
// Iconos disponibles (ampliados para metas latinas).
const ICONS = [
  { e: '🛡️', label: 'Fondo de emergencia' },
  { e: '💳', label: 'Libre de deudas' },
  { e: '🏠', label: 'Casa' },
  { e: '🚗', label: 'Carro' },
  { e: '🏍️', label: 'Moto' },
  { e: '✈️', label: 'Viaje' },
  { e: '🎓', label: 'Estudios' },
  { e: '💍', label: 'Boda' },
  { e: '🎉', label: 'Quinceañera' },
  { e: '👶', label: 'Bebé' },
  { e: '💼', label: 'Negocio' },
  { e: '🎄', label: 'Navidad' },
  { e: '🏥', label: 'Salud' },
  { e: '💻', label: 'Tecnología' },
  { e: '🏖️', label: 'Vacaciones' },
];

// Metas mas comunes en Latinoamerica — chips para arrancar con un toque.
// (nombre + icono coherentes; el monto/fecha los pone el cliente).
const PRESETS = [
  { name: 'Fondo de emergencia', e: '🛡️' },
  { name: 'Cuota inicial casa', e: '🏠' },
  { name: 'Carro', e: '🚗' },
  { name: 'Viaje', e: '✈️' },
  { name: 'Estudios', e: '🎓' },
  { name: 'Mi negocio', e: '💼' },
  { name: 'Boda', e: '💍' },
  { name: 'Quinceañera', e: '🎉' },
  { name: 'Navidad', e: '🎄' },
];

// Nombres "por defecto" (de iconos y presets): si el nombre actual es uno de
// estos, tocar otro icono tambien actualiza el nombre para que SIEMPRE concuerde.
// Si el cliente escribio un nombre propio, se respeta (solo cambia el icono).
const KNOWN_NAMES = new Set([
  ...ICONS.map((i) => i.label.toLowerCase()),
  ...PRESETS.map((p) => p.name.toLowerCase()),
]);

export default function GoalNew() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const canSaveMonthly = Number(searchParams.get('ahorro')) || 0; // del diagnostico
  // Pre-llenado desde el diagnostico (ej. "Fondo de emergencia", "Libre de deudas")
  const _presetName = searchParams.get('nombre') || '';
  const _presetIcon = /emergencia/i.test(_presetName) ? '🛡️' : /deuda/i.test(_presetName) ? '💳' : '🏠';
  const [name, setName] = useState(_presetName);
  const [icon, setIcon] = useState(_presetIcon);
  const [amount, setAmount] = useState(searchParams.get('monto') || '');
  const [date, setDate] = useState('');
  const [plan, setPlan] = useState(null);
  const [showIcons, setShowIcons] = useState(false); // selector de iconos colapsado por defecto
  const [portfolios, setPortfolios] = useState([]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  // Rentabilidad historica real de los portafolios (para transparencia).
  useEffect(() => {
    let alive = true;
    getPortfolioReturns()
      .then((r) => alive && setPortfolios(r.data || []))
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  const portfolioInfo = plan ? portfolios.find((p) => p.key === plan.portfolioType) : null;

  // Preview en vivo (plazo, portafolio, aporte/mes) cada vez que cambian monto o fecha.
  useEffect(() => {
    const amt = Number(amount);
    if (!amt || amt <= 0 || !date) { setPlan(null); return; }
    let alive = true;
    previewGoal({ targetAmountCop: amt, targetDate: date })
      .then((p) => alive && setPlan(p))
      .catch(() => alive && setPlan(null));
    return () => { alive = false; };
  }, [amount, date]);

  const handleCreate = async () => {
    setErr(null);
    if (!name.trim() || !Number(amount) || !date) {
      setErr('Completa el nombre, el monto y la fecha.');
      return;
    }
    setSaving(true);
    try {
      const g = await createGoal({
        name: name.trim(),
        targetAmountCop: Number(amount),
        targetDate: date,
        icon,
        monthlyTargetCop: plan?.suggestedMonthlyCop,
      });
      navigate(`/dashboard/metas/${g._id}`);
    } catch (e) {
      setErr(e.response?.data?.message || e.message);
    } finally {
      setSaving(false);
    }
  };

  const minDate = new Date(Date.now() + 30 * 24 * 3600 * 1000).toISOString().slice(0, 10);

  return (
    <div className="px-4 sm:px-10 py-6 max-w-2xl mx-auto" style={{ background: SOFT }}>
      <button onClick={() => navigate('/dashboard/metas')} className="text-xs text-[#6B7280] hover:underline mb-4">
        ← Mis Metas
      </button>
      <h1 className="text-xl font-bold text-[#181D27] mb-1">Nueva meta</h1>
      <p className="text-sm text-[#6B7280] mb-6">Dinos qué quieres lograr y te decimos cuánto ahorrar al mes.</p>

      <div className="bg-white border border-[#EFF1F4] rounded-xl px-6 py-5 space-y-5">
        {/* Metas populares en Latam — un toque llena nombre + icono coherentes */}
        <div>
          <div className="text-xs font-medium text-[#6B7280] mb-2">Metas populares — toca una para empezar</div>
          <div className="flex flex-wrap gap-2">
            {PRESETS.map((p) => (
              <button
                key={p.name}
                type="button"
                onClick={() => { setName(p.name); setIcon(p.e); }}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition ${name === p.name ? 'border-[#2A59C6] bg-[#EAF0FF] text-[#2A59C6]' : 'border-[#EFF1F4] bg-white text-[#374151] hover:border-[#2A59C6]'}`}
              >
                <span className="text-base leading-none">{p.e}</span>{p.name}
              </button>
            ))}
          </div>
        </div>

        {/* Nombre + icono */}
        <div>
          <label className="block text-xs font-medium text-[#6B7280] mb-1">¿Para qué es tu meta?</label>
          <div className="flex items-center gap-2">
            <span className="text-2xl leading-none w-9 h-9 flex items-center justify-center rounded-lg bg-[#F0F4FF] shrink-0">{icon}</span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Cuota inicial casa"
              className="flex-1 border border-[#EFF1F4] rounded-lg px-3 py-2 text-sm text-[#181D27] focus:outline-none focus:border-[#2A59C6]"
            />
          </div>
          {/* Selector de iconos COLAPSADO por defecto — los chips de arriba ya
              asignan icono; esto es solo para metas personalizadas o cambiarlo. */}
          <button
            type="button"
            onClick={() => setShowIcons((v) => !v)}
            className="text-xs text-[#2A59C6] font-medium mt-3 hover:underline"
          >
            {showIcons ? 'Ocultar íconos' : 'Cambiar ícono'}
          </button>
          {showIcons && (
            <div className="flex flex-wrap gap-2 mt-2">
              {ICONS.map((it) => (
                <button
                  key={it.e}
                  type="button"
                  onClick={() => { setIcon(it.e); const n = name.trim().toLowerCase(); if (!n || KNOWN_NAMES.has(n)) setName(it.label); }}
                  className={`w-10 h-10 rounded-lg text-xl flex items-center justify-center border-2 transition ${icon === it.e ? 'border-[#2A59C6] bg-[#EAF0FF] scale-110' : 'border-[#EFF1F4] bg-white'}`}
                >
                  {it.e}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Monto */}
        <div>
          <label className="block text-xs font-medium text-[#6B7280] mb-1">¿Cuánto necesitas? (en pesos)</label>
          <input
            type="number"
            inputMode="numeric"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="30000000"
            className="w-full border border-[#EFF1F4] rounded-lg px-3 py-2 text-sm text-[#181D27] focus:outline-none focus:border-[#2A59C6]"
          />
          {Number(amount) > 0 && <div className="text-xs text-[#6B7280] mt-1">{fmtCop(amount)}</div>}
        </div>

        {/* Fecha */}
        <div>
          <label className="block text-xs font-medium text-[#6B7280] mb-1">¿Para cuándo la quieres?</label>
          <input
            type="date"
            value={date}
            min={minDate}
            onChange={(e) => setDate(e.target.value)}
            className="w-full border border-[#EFF1F4] rounded-lg px-3 py-2 text-sm text-[#181D27] focus:outline-none focus:border-[#2A59C6]"
          />
        </div>

        {/* Preview del plan */}
        {plan && (
          <div className="rounded-lg px-5 py-4 text-white" style={{ background: NAVY }}>
            <div className="text-xs opacity-80 mb-1">Para lograrlo, ahorra</div>
            <div className="text-2xl font-bold mb-2">{fmtCop(plan.suggestedMonthlyCop)} <span className="text-sm font-normal opacity-80">/ mes</span></div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs opacity-90">
              <span>Plazo: {horizonLabel(plan.horizon)} ({plan.monthsLeft} meses)</span>
              <span>Portafolio sugerido: {portfolioLabel(plan.portfolioType)}</span>
            </div>
            <div className="text-[11px] opacity-70 mt-2">
              {portfolioInfo && portfolioInfo.historicalPct != null ? (
                <>El portafolio {portfolioLabel(plan.portfolioType)} ha rendido <b>~{portfolioInfo.historicalPct}% al año</b> histórico. Proyectamos conservador con {plan.annualReturnPct}%. </>
              ) : (
                <>Estimado con rentabilidad conservadora ({plan.annualReturnPct}% anual). </>
              )}
              No es una promesa de rendimiento.
            </div>
          </div>
        )}

        {/* Comparacion con lo que puede ahorrar (viene del diagnostico) */}
        {plan && canSaveMonthly > 0 && (
          canSaveMonthly >= plan.suggestedMonthlyCop ? (
            <div className="text-xs rounded-lg px-3 py-2 bg-[#F0FDF4] text-[#166534] border border-[#BBF7D0]">
              Dijiste que puedes ahorrar {fmtCop(canSaveMonthly)}/mes y esta meta pide {fmtCop(plan.suggestedMonthlyCop)}/mes. Te alcanza cómodo.
            </div>
          ) : (
            <div className="text-xs rounded-lg px-3 py-2 bg-[#FFFBEB] text-[#92400E] border border-[#FDE68A]">
              Esta meta pide {fmtCop(plan.suggestedMonthlyCop)}/mes, pero dijiste que puedes ahorrar {fmtCop(canSaveMonthly)}/mes. Prueba bajar el monto o darte más tiempo.
            </div>
          )
        )}

        {err && <div className="text-sm text-red-600">{err}</div>}

        <button
          onClick={handleCreate}
          disabled={saving}
          className="w-full py-2.5 rounded-lg text-white font-medium disabled:opacity-60"
          style={{ background: NAVY }}
        >
          {saving ? 'Creando...' : 'Crear meta'}
        </button>
      </div>
    </div>
  );
}
